<template>
  <div :class="$style.imageZoomer">
    <aw-vue-img-zoomer-fork
      ref="zoomComp"
      :regular="url"
      :regular-webp="webpUrl"
      :zoom-amount="zoom"
      :show-message="false"
      :show-message-touch="false"
      :tap-to-close="true"
      :lazyload="lazyload"
      :img-tag-bind="imgTagBind"
      @on-zoom="handleZoomOn()"
    />
  </div>
</template>

<script setup>
  import { ref, watch, onMounted } from 'vue';
  import AwVueImgZoomerFork from '~~/common/components/AwVueImgZoomerFork.vue';

  const props = defineProps({
    zoom: {
      type: Number,
      default: 1,
    },
    url: {
      type: String,
      required: true,
    },
    webpUrl: {
      type: String,
      default: null,
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    imgTagBind: {
      type: Object,
      default: null,
    },
  });

  const zoomComp = ref(null);

  onMounted(() => {
    overrideZoomComponent();
  });

  watch(() => props.zoom, (newVal) => {
    zoomComp.value?.isZoom?.(newVal > 0, 'hover');
  });

  function handleZoomOn () {
    const zoomCompRef = zoomComp.value;
    if (!zoomCompRef) {
      return;
    }
    zoomCompRef.x = Math.abs(zoomCompRef.cx);
    zoomCompRef.y = Math.abs(zoomCompRef.cy);
  }

  function overrideZoomComponent () {
    const zoomCompRef = zoomComp.value;
    if (!zoomCompRef) {
      return;
    }

    if (zoomCompRef.$el?.nextElementSibling?.clickOutsideEvent) {
      document.body.removeEventListener('click', zoomCompRef.$el.nextElementSibling.clickOutsideEvent);
    }

    const originalIsZoom = zoomCompRef.isZoom;
    zoomCompRef.isZoom = (type, action) => {
      if (type && action === 'hover' && props.zoom <= 1) {
        return;
      }
      if (!type) {
        zoomCompRef.x = Math.abs(zoomCompRef.cx);
        zoomCompRef.y = Math.abs(zoomCompRef.cy);
        return;
      }
      return originalIsZoom(type, action);
    };

    const originalMousePos = zoomCompRef.mousePos;
    zoomCompRef.mousePos = (e) => {
      if (props.zoom <= 1) {
        return;
      }
      return originalMousePos(e);
    };

    const overrideGetOptions = () => {
      zoomCompRef.options.zoomAmount = zoomCompRef.zoomAmount;
      zoomCompRef.options.zoom = zoomCompRef.regular;
      zoomCompRef.options.zoomWebp = zoomCompRef.regularWebp;

      zoomCompRef.resize();
    };
    const overrideResize = () => {
      zoomCompRef.zoomed = props.zoom > 1;
      zoomCompRef.loaded = false;
      if (zoomCompRef.zoomed) {
        zoomCompRef.loadZoom();
      }
      zoomCompRef.x = Math.abs(zoomCompRef.cx);
      zoomCompRef.y = Math.abs(zoomCompRef.cy);
    };
    zoomCompRef.get_options = overrideGetOptions;
    zoomCompRef.resize = overrideResize;

    const originalOffset = zoomCompRef.offset;
    zoomCompRef.offset = () => {
      try {
        originalOffset();
      } catch (e) { /* noop */
      }
      return originalOffset;
    };

    const originalMobilePos = zoomCompRef.mobilePos;
    zoomCompRef.mobilePos = () => {
      originalMobilePos();
      if (!zoomCompRef.touch) {
        zoomCompRef.x = Math.abs(zoomCompRef.cx);
        zoomCompRef.y = Math.abs(zoomCompRef.cy);
      }
    };

    zoomCompRef.get_options();
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.imageZoomer {
  :global(.vh--loading-o) {
    display: none;
  }
}
</style>
